import React, { useState, useMemo, useRef } from "react";
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, Platform, Pressable } from "react-native";
import { useTheme, Portal } from "react-native-paper";
import i18n from "../i18n";
import PropTypes from "prop-types";

const AppSelector = ({ additionalStyle = {}, options, value = [], onChange, editable = true, multiple = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
    const theme = useTheme();
    const selectorRef = useRef(null);

    const styles = useMemo(() => StyleSheet.create({
        container: {
            width: '100%',
            position: 'relative',
            zIndex: Platform.OS === 'web' ? 1 : undefined,
            ...additionalStyle
        },
        selector: {
            borderWidth: 1,
            borderColor: theme.colors.tertiary,
            borderRadius: 15,
            backgroundColor: editable ? 'white' : '#f5f5f5',
            minHeight: 50,
            justifyContent: 'center',
            paddingHorizontal: 15,
        },
        selectorText: {
            textAlign: "center",
            fontFamily: "NotoSans-Regular",
            fontSize: 18,
            color: editable ? 'black' : '#666',
        },
        placeholder: {
            color: '#999',
            fontStyle: 'italic',
        },
        dropdown: {
            position: 'absolute',
            backgroundColor: '#FFFFFF',
            borderWidth: 1,
            borderColor: theme.colors.tertiary,
            borderRadius: 15,
            maxHeight: 200,
            zIndex: 1000,
            width: dropdownPosition.width,
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            ...Platform.select({
                web: {
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.25,
                    shadowRadius: 3.84,
                },
                default: {
                    elevation: 5,
                },
            }),
        },
        option: {
            paddingVertical: 15,
            paddingHorizontal: 15,
            borderBottomWidth: 1,
            borderBottomColor: '#f0f0f0',
            backgroundColor: '#FFFFFF',
        },
        optionSelected: {
            backgroundColor: theme.colors.primary + '20',
        },
        optionText: {
            textAlign: 'center',
            fontFamily: "NotoSans-Regular",
            fontSize: 16,
        },
        optionTextSelected: {
            fontWeight: 'bold',
            color: theme.colors.primary,
        },
        backdrop: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255,255,255,0.85)',
            zIndex: 999,
        },
    }), [theme, additionalStyle, editable, dropdownPosition]);

    const getDisplayText = () => {
        if (!multiple) {
            if (!value) return i18n.t('commonForm.selectorPlaceholder');
            const selectedKey = Object.keys(options).find(key => options[key] === value);
            return selectedKey || i18n.t('commonForm.selectorPlaceholder');
        }

        if (!value || value.length === 0) {
            return i18n.t('commonForm.selectorPlaceholder');
        }

        if (value.length === 1) {
            const selectedKey = Object.keys(options).find(key => options[key] === value[0]);
            return selectedKey || i18n.t('commonForm.selectorPlaceholder');
        }

        return i18n.t('commonForm.NElementsSelected', { count: value.length });
    };

    const handleOptionPress = (optionValue) => {
        if (!multiple) {
            onChange(optionValue);
            setIsOpen(false);
            return;
        }

        const newValue = [...(value || [])];
        const index = newValue.indexOf(optionValue);

        if (index > -1) {
            newValue.splice(index, 1);
        } else {
            newValue.push(optionValue);
        }

        onChange(newValue);
    };

    const isSelected = (optionValue) => {
        if (!multiple) return value === optionValue;
        return value && value.includes(optionValue);
    };

    const displayText = getDisplayText();
    const isPlaceholder = displayText === i18n.t('commonForm.selectorPlaceholder');

    const openDropdown = () => {
        if (Platform.OS !== 'web') {
            setIsOpen(true);
            return;
        }

        const rect = selectorRef.current?.getBoundingClientRect?.();
        if (rect) {
            setDropdownPosition({
                top: rect.bottom + window.scrollY + 5,
                left: rect.left + window.scrollX,
                width: rect.width,
            });
            setIsOpen(true);
        }
    };

    const renderDropdown = () => (
        <Portal>
            <Pressable style={styles.backdrop} onPress={() => setIsOpen(false)} />
            <View style={styles.dropdown}>
                <ScrollView showsVerticalScrollIndicator={false}>
                    {Object.keys(options).map((label) => {
                        const optionValue = options[label];
                        const selected = isSelected(optionValue);

                        return (
                            <TouchableOpacity
                                key={optionValue}
                                style={[
                                    styles.option,
                                    selected && styles.optionSelected
                                ]}
                                onPress={() => handleOptionPress(optionValue)}
                            >
                                <Text style={[
                                    styles.optionText,
                                    selected && styles.optionTextSelected
                                ]}>
                                    {label}
                                </Text>
                            </TouchableOpacity>
                        );
                    })}
                </ScrollView>
            </View>
        </Portal>
    );

    return (
        <View style={styles.container}>
            <TouchableOpacity
                ref={selectorRef}
                style={styles.selector}
                onPress={() => editable && openDropdown()}
                disabled={!editable}
            >
                <Text style={[
                    styles.selectorText,
                    isPlaceholder && styles.placeholder
                ]}>
                    {displayText}
                </Text>
            </TouchableOpacity>

            {isOpen && editable && renderDropdown()}
        </View>
    );
};

AppSelector.propTypes = {
    additionalStyle: PropTypes.object,
    options: PropTypes.object.isRequired,
    value: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    editable: PropTypes.bool,
    multiple: PropTypes.bool,
};

export default AppSelector;
